import { useStyles } from '../assets/LoginStyle.jsx';
import { Box, Button, Container, Dialog, Link, TextField, Typography } from '@mui/material';
import { VBox } from '../assets/SharedComponents';
import ReactCodeInput from 'react-verification-code-input';
import { useTranslation } from 'react-i18next';
import { CustomBusy } from './Common/CustomBusy.jsx';
import { useEffect, useState, useRef } from 'react';
import ReLinkCounter from './Common/ReLinkCounter.jsx';
import usePhoneVerification from 'hooks/usePhoneVerification';
import { useSnackbar } from 'notistack';
import { isEmail } from 'validator';
import getSMS from '../assets/getSMS.svg';
import wrongSmsCode from '../assets/wrongSmsCode.svg';
import MailSuccess from '../assets/MailSuccess.svg';
import useApi from 'hooks/useApi.js';
import { resetPassViaEmail } from 'api/api.js';
import useAuth0Feathers from 'hooks/useAuth0Feathers.js';
import { feathersClient } from 'api/client';

export const SmsVerification = ({
    type,
    PhoneNumber,
    username,
    handleNoMatchingParentFound,
    setLoginIdProcess,
    setEmail,
    changePhoneValue
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { sendOTP, verifyOTP, loading: otpLoading } = usePhoneVerification();
    const { enqueueSnackbar } = useSnackbar();
    const [visibleResend, setVisibleResend] = useState(false);
    const [wrongPinInput, setWrongPinInput] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [mailValid, setMailValid] = useState(true);
    const [resetByMail, setResetByMail] = useState(false);
    const [emailAddress, setEmailAddress] = useState("");
    const [errMessage, setErrMessage] = useState("");
    const [busy, setBusy] = useState(false);
    const [id, setId] = useState("");
    const [emailWasSent, setEmailWasSent] = useState(false);
    const [maskedEmail, setMaskedEmail] = useState(false);
    const [errorByEmail, setErrorByEmail] = useState(false);
    const [VerificationState, setVerificationState] = useState({
        VerificationValue: "",
        VerificationPress: false
    });
    const pinInputRef = useRef(null);
    const hookAUTH = useAuth0Feathers();
    const isForgotPass = type === 'forgotPass';
    const inputRef1 = useRef();
    const inputRef2 = useRef();
    const resetViaEmail = useApi(resetPassViaEmail);

    useEffect(() => {
        setTimeout(() => {
            try {
                pinInputRef.current.iRefs[0].current.focus();
            } catch (error) {
                console.log(error);
            }
        }, 500);
        resetView();
    }, []);

    const resetView = () => {
        setBusy(false);
        setWrongPinInput(false);
        setErrMessage("");
        setVerificationState({
            VerificationValue: "",
            VerificationPress: false
        });
        try {
            pinInputRef.current.__clearvalues__();
        } catch (error) {
            console.log(error);
        }
    };

    const loginPhoneHandler = async (code) => {
        try {
            setBusy(true);
            setWrongPinInput(false);

            console.log('🔐 Verifying OTP for login...');

            // Verify OTP with Auth0
            const otpResult = await verifyOTP(PhoneNumber.phone, code, username);

            console.log('✅ OTP verified, authenticating with Feathers...');

            // Authenticate with Feathers using Auth0 token
            const authResult = await feathersClient.authenticate({
                strategy: 'auth0',
                access_token: otpResult.access_token,
                username: username,
                phoneNumber: otpResult.phone_number
            });

            console.log('✅ Authenticated successfully');

            // Get user info
            const user = authResult.user || authResult;

            // Handle forgot password flow
            if (isForgotPass) {
                if (user.email && user.email_verified) {
                    console.log('📧 Sending password reset email to:', user.email);

                    try {
                        // Send password reset email via Auth0
                        await feathersClient.service('send-reset-password-auth0').create({
                            data: { email: user.email }
                        });

                        setEmail(user.email);
                        setLoginIdProcess(5);

                        window.localStorage.clear();
                    } catch (error) {
                        console.error('❌ Password reset email failed:', error);
                        const msg = error.message || t("sendCodeFailed");
                        setErrMessage(msg);
                        enqueueSnackbar(msg, { variant: 'error' });
                    }
                } else {
                    // Email not verified, can't reset password
                    throw new Error(t("emailNotVerified") || "Email not verified");
                }
            } else {
                // ⬇️ SIMPLIFIED: Let the hook handle navigation
                console.log('✅ Normal login - delegating to hook...');
                await hookAUTH.handleSuccessfulLogin(authResult, username);
            }

        } catch (error) {
            console.error('❌ Login failed:', error);

            if (error.data && error.data.noMatchingParentFound) {
                // No matching parent for ID and phone number
                resetView();
                handleNoMatchingParentFound(error.data.username, error.data.phone);
            } else {
                // Show error
                const msg = error.message || t("invalidCode");
                setErrMessage(msg);
                handleWrongPinInput();
            }
        } finally {
            setBusy(false);
        }
    };

    const handleWrongPinInput = () => {
        setWrongPinInput(true);
        const inputElement = pinInputRef.current;
        inputElement.__clearvalues__();
        setVerificationState({
            VerificationValue: "",
            VerificationPress: false
        });
    };

    const reSendVerification = async () => {
        try {
            setWrongPinInput(false);

            console.log('📱 Resending OTP...');

            // Format and send OTP
            await sendOTP(PhoneNumber.phone);

            setVerificationState({ ...VerificationState, Resend: true });

        } catch (error) {
            console.error('❌ Resend failed:', error);
            // Error already shown by hook
        }
    };

    const signInByMail = async () => {
        try {
            const sPhoneNum = PhoneNumber.phone.toString();
            let phone = sPhoneNum.startsWith('0')
                ? sPhoneNum.slice(1)
                : sPhoneNum;

            console.log('📧 Sending magic link email...');

            // Send magic link via Auth0
            await feathersClient.service('send-auth0-signin-email').create({
                username: username,
                email: emailAddress,
                phoneNumber: `+972${phone}`
            });

            // Save to localStorage so the landing page can verify without asking for email again
            window.localStorage.setItem('emailForSignIn', emailAddress);
            window.localStorage.setItem('usernameForSignIn', username);
            window.localStorage.setItem('phoneForSignIn', `+972${phone}`);

            setVerificationState({ ...VerificationState, mailLink: true });
            setOpenDialog(false);

        } catch (error) {
            console.error('❌ Magic link failed:', error);
            enqueueSnackbar(
                t("errorOccuredText") || "אירעה שגיאה",
                { variant: 'error' }
            );
        }
    };

    const setAndValidateMail = (str) => {
        let mailReg = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
        setEmailAddress(str);

        if (isEmail(str) && mailReg.test(str)) {
            setMailValid(true);
        } else {
            setMailValid(false);
        }
    };

    const sendPasswordLinkToEmail = () => {
        setResetByMail(true);
    };

    const handleId = (e) => {
        let temp = e.target.value.slice(0, 30);
        setId(temp);
    };

    const sendToEmailResetLink = async () => {
        await hookAUTH.loginAnonymously();

        try {
            setBusy(true);

            console.log('📧 Sending password reset email...');

            const phonenumber = PhoneNumber.phone;
            const data = { phonenumber, username: id };

            // Call backend service (now uses Auth0 instead of Firebase)
            const res = await resetViaEmail.requestPromise(data);

            console.log('✅ Email sent:', res);

            if (res[0].statusCode === 202) {
                setEmailWasSent(true);
                setMaskedEmail(res[0].maskedEmail);
            } else {
                setErrorByEmail(true);
            }

        } catch (error) {
            console.error('❌ Password reset email failed:', error);
            setErrorByEmail(true);
        } finally {
            await hookAUTH.logoutAnonymously();
            setBusy(false);
        }
    };

    return (
        <>
            {!resetByMail ? (
                <Box width="100%" display='inline-grid' flexDirection='column'>
                    {!wrongPinInput ? (
                        <VBox alignItems="center">
                            <Typography
                                className={`${classes.paddingBottom1rem} ${classes.paddingTop2_5rem} ${classes.fontSize1_5}`}
                                sx={{ fontWeight: "bold" }}
                            >
                                {isForgotPass ? t("forgotPassword") : t("enterWithOTP")}
                            </Typography>

                            {!VerificationState?.mailLink && (
                                <img alt="SMS" width="200px" height="120px" src={getSMS} />
                            )}

                            {VerificationState?.mailLink && (
                                <img alt="Mail Success" width="200px" height="120px" src={MailSuccess} />
                            )}

                            {!VerificationState?.mailLink && (
                                <>
                                    <Typography align="center" className={classes.paddingBottom1rem}>
                                        {VerificationState?.Resend ? (
                                            <>{`${t("resendingCodeToPhone")} ${t("another")} ${t("toPhoneNum")}`}</>
                                        ) : (
                                            `${t("sendingCodeToPhone")} ${t("toPhoneNum")}`
                                        )}
                                        <b className={classes.phoneDir}> {PhoneNumber.phone} </b>
                                    </Typography>
                                    <Typography align="center" className={classes.paddingBottom1rem}>
                                        {t("pleaseEnterCode")}
                                    </Typography>
                                </>
                            )}

                            {VerificationState?.mailLink && (
                                <Typography
                                    align="center"
                                    className={classes.paddingBottom1rem}
                                    sx={{ fontWeight: "bold" }}
                                >
                                    {`${t("inThisMoment")} ${t("linkPop")}`}
                                </Typography>
                            )}
                        </VBox>
                    ) : (
                        <VBox alignItems="center">
                            {!VerificationState?.mailLink && (
                                <Typography
                                    className={`${classes.paddingTop2_5rem} ${classes.fontSize1_5}`}
                                    sx={{ fontWeight: "bold", color: "#ff0000" }}
                                >
                                    {t("errorOccured")}
                                </Typography>
                            )}

                            {errMessage && (
                                <Typography
                                    align="center"
                                    className={classes.paddingBottom1rem}
                                    sx={{ fontWeight: "bold", color: "#ff0000" }}
                                >
                                    {errMessage}
                                </Typography>
                            )}

                            {!VerificationState?.mailLink && (
                                <img alt="Wrong Code" width="200px" height="120px" src={wrongSmsCode} />
                            )}

                            {VerificationState?.mailLink && (
                                <img alt="Mail Success" width="200px" height="120px" src={MailSuccess} />
                            )}

                            {!VerificationState?.mailLink && (
                                <Typography align="center" className={classes.paddingBottom1rem}>
                                    {t("reEnterCode")}
                                </Typography>
                            )}

                            {VerificationState?.mailLink && (
                                <Typography
                                    align="center"
                                    className={classes.paddingBottom1rem}
                                    sx={{ fontWeight: "bold" }}
                                >
                                    {`${t("inThisMoment")} ${t("linkPop")}`}
                                </Typography>
                            )}
                        </VBox>
                    )}

                    {isForgotPass && (
                        <Link
                            onClick={sendPasswordLinkToEmail}
                            color='#007f00'
                            className={`${classes.CenterItems} ${classes.LinkStyle} ${classes.marginTop1} ${classes.paddingBottom1rem}`}
                            sx={{ fontSize: "0.875rem !important", cursor: 'pointer' }}
                        >
                            {t("resetPasswordByMail")}
                        </Link>
                    )}

                    {!VerificationState?.mailLink && (
                        <VBox alignItems="center" style={{ direction: 'ltr', marginBottom: "0.5rem" }}>
                            <ReactCodeInput
                                onComplete={loginPhoneHandler}
                                autoComplete={false}
                                type='number'
                                inputMode="numeric"
                                autoFocus={true}
                                fields={6}
                                ref={pinInputRef}
                                color='black'
                                className={classes.codePinInputStyle}
                                disabled={busy || otpLoading}
                            />
                        </VBox>
                    )}

                    <VBox justifyContent='space-around'>
                        {(busy || otpLoading) && <CustomBusy />}

                        {!VerificationState?.mailLink && (
                            <VBox alignItems="center" justifyContent="center" marginTop="0.2rem">
                                {!visibleResend ? (
                                    <ReLinkCounter setVisible={setVisibleResend} />
                                ) : (
                                    <Link
                                        onClick={reSendVerification}
                                        color='#007f00'
                                        className={`${classes.CenterItems} ${classes.LinkStyle}`}
                                        sx={{ fontSize: "0.875rem !important", cursor: 'pointer' }}
                                    >
                                        {t("resendToken")}
                                    </Link>
                                )}
                            </VBox>
                        )}

                        {!VerificationState?.mailLink && !isForgotPass && (
                            <Button
                                onClick={() => setOpenDialog(true)}
                                sx={{ textDecoration: "underline" }}
                            >
                                {t("didntGetSMS")}
                            </Button>
                        )}

                        {openDialog && (
                            <SentToEmailDialog
                                openDialog={openDialog}
                                setOpenDialog={setOpenDialog}
                                emailAddress={emailAddress}
                                mailValid={mailValid}
                                setAndValidateMail={setAndValidateMail}
                                signInByMail={signInByMail}
                            />
                        )}
                    </VBox>
                </Box>
            ) : (
                <Box width="100%" display='inline-grid' flexDirection='column'>
                    <VBox alignItems="center">
                        <Typography
                            className={`${classes.paddingBottom1rem} ${classes.paddingTop2_5rem} ${classes.fontSize1_5}`}
                            sx={{ fontWeight: "bold" }}
                        >
                            {t("forgotPassword")}
                        </Typography>

                        {!emailWasSent ? (
                            <>
                                <Typography align="center" className={`${classes.paddingBottom1rem} ${classes.margin0_3}`}>
                                    {t("pleaseEnterYourId")}
                                </Typography>

                                {errorByEmail && (
                                    <>
                                        <Typography sx={{ fontWeight: "bold", color: "#ff0000" }}>
                                            {t("detailsError")}
                                        </Typography>
                                        <Typography sx={{ fontWeight: "bold", color: "#ff0000" }}>
                                            {t("cantSendTheEmail")}
                                        </Typography>
                                    </>
                                )}

                                <TextField
                                    type="number"
                                    name="Id"
                                    label={t("parentIdDoc")}
                                    placeholder={t("idPlaceholder")}
                                    style={{ width: "100% !important" }}
                                    value={id}
                                    className={`${classes.paddingBottom1rem} ${classes.RemoveTextFieldNumberArrow}`}
                                    onChange={handleId}
                                    variant="outlined"
                                    InputProps={{
                                        inputProps: {
                                            inputMode: 'tel',
                                            pattern: "[0-9]*"
                                        },
                                        inputRef: inputRef1
                                    }}
                                    onWheel={(e) => {
                                        if (inputRef1?.current?.blur) {
                                            inputRef1.current.blur();
                                        }
                                    }}
                                />

                                <TextField
                                    placeholder={t("matchPhonePlaceholder")}
                                    style={{ width: "100% !important" }}
                                    className={`${classes.paddingBottom1rem} ${classes.RemoveTextFieldNumberArrow}`}
                                    InputProps={{
                                        inputProps: {
                                            inputMode: 'tel',
                                            pattern: "[0-9]*"
                                        },
                                        maxLength: 20,
                                        type: "number",
                                        inputRef: inputRef2
                                    }}
                                    onWheel={(e) => {
                                        if (inputRef2?.current?.blur) {
                                            inputRef2.current.blur();
                                        }
                                    }}
                                    label={t("phoneNumber")}
                                    onChange={e => changePhoneValue(e, 'phone')}
                                    value={PhoneNumber.phone}
                                    disabled={busy}
                                    variant="outlined"
                                />

                                {busy ? (
                                    <CustomBusy />
                                ) : (
                                    <Button
                                        disabled={!id || !PhoneNumber.phone}
                                        sx={{ marginTop: "1rem" }}
                                        className={!id || !PhoneNumber.phone ? classes.buttonGreenLight : classes.buttonGreen}
                                        variant="contained"
                                        onClick={sendToEmailResetLink}
                                    >
                                        {t("sendLinkToMail")}
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                <img alt="Mail Success" width="200px" height="120px" src={MailSuccess} />
                                <Typography>{t("inThisMomentWeSend")}</Typography>
                                <Typography>{maskedEmail}</Typography>
                            </>
                        )}
                    </VBox>
                </Box>
            )}
        </>
    );
};

const SentToEmailDialog = ({
    openDialog,
    setOpenDialog,
    emailAddress,
    mailValid,
    setAndValidateMail,
    signInByMail
}) => {
    const { t } = useTranslation();

    return (
        <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            PaperProps={{
                sx: {
                    borderRadius: "40px !important",
                    backgroundColor: "#f0f3f0",
                    width: "16rem",
                    height: "20rem"
                }
            }}
        >
            <Container>
                <Box display="flex" flexDirection="column" alignItems='center' justifyContent='center' mt="2rem">
                    <Typography sx={{ marginBottom: "1rem" }}>
                        {t("pleaseEnterMail")}
                    </Typography>

                    <TextField
                        name="emailAddress"
                        width='50%'
                        label={t("email")}
                        variant="outlined"
                        value={emailAddress}
                        error={!mailValid}
                        onChange={e => setAndValidateMail(e.target.value)}
                        InputProps={{
                            inputProps: {
                                inputMode: 'url'
                            }
                        }}
                    />

                    <Button
                        onClick={signInByMail}
                        disabled={!mailValid || !emailAddress.length}
                        sx={{ marginTop: "1rem" }}
                    >
                        {t("send1")}
                    </Button>
                </Box>
            </Container>
        </Dialog>
    );
};
